import axios from "axios";
import { getAuth } from "firebase/auth";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname || "";
    const isLocal =
      hostname.includes("localhost") || hostname.includes("127.0.0.1");

    if (isLocal) {
      return "http://127.0.0.1:5001/damia-gcp/us-central1/api";
    }
  }

  return "https://us-central1-damia-gcp.cloudfunctions.net/api";
};

const API_BASE_URL = getApiBaseUrl();

const RealTimeVoiceService = {
  /**
   * Get OpenAI ephemeral token for real-time voice
   */
  getVoiceToken: async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await axios.post(
        `${API_BASE_URL}/getTempSpeechTokenSDR`,
        {},
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting voice token:", error.response || error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          "Failed to get voice token"
      );
    }
  },

  /**
   * Initiate outbound call via Twilio
   * @param {Object} params
   * @param {string} params.teamId - Team ID
   * @param {string} params.prospectId - Prospect/Contact ID
   * @param {string} params.phoneNumber - Phone number to call
   * @param {string} params.mode - "voicemail" or "realtime"
   * @param {string} [params.voicemailScriptId] - Optional voicemail script ID
   * @param {string} [params.voicemailScript] - Optional voicemail script text
   */
  initiateCall: async (params) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (typeof window !== "undefined") {
        try {
          const auth = getAuth();
          const currentUser = auth.currentUser;

          if (currentUser) {
            const token = await currentUser.getIdToken(true);
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (tokenError) {
          console.warn("Unable to obtain auth token for call:", tokenError);
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/initiateCall`,
        params,
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error("Error initiating call:", error.response || error);

      const message =
        (typeof error.response?.data === "string"
          ? error.response.data
          : null) ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to initiate call. Please try again.";

      throw new Error(message);
    }
  },

  /**
   * Get call details
   * @param {string} teamId - Team ID
   * @param {string} callId - Call ID
   */
  getCallDetails: async (teamId, callId) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (typeof window !== "undefined") {
        try {
          const auth = getAuth();
          const currentUser = auth.currentUser;

          if (currentUser) {
            const token = await currentUser.getIdToken(true);
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (tokenError) {
          console.warn("Unable to obtain auth token:", tokenError);
        }
      }

      const response = await axios.get(
        `${API_BASE_URL}/getCallDetails?teamId=${teamId}&callId=${callId}`,
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting call details:", error.response || error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          "Failed to get call details"
      );
    }
  },

  /**
   * Get call history for a team
   * @param {string} teamId - Team ID
   * @param {number} [limit=50] - Number of calls to fetch
   */
  getCallHistory: async (teamId, limit = 50) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (typeof window !== "undefined") {
        try {
          const auth = getAuth();
          const currentUser = auth.currentUser;

          if (currentUser) {
            const token = await currentUser.getIdToken(true);
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (tokenError) {
          console.warn("Unable to obtain auth token:", tokenError);
        }
      }

      const response = await axios.get(
        `${API_BASE_URL}/getCallHistory?teamId=${teamId}&limit=${limit}`,
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting call history:", error.response || error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          "Failed to get call history"
      );
    }
  },

  /**
   * Update call notes
   * @param {string} teamId - Team ID
   * @param {string} callId - Call ID
   * @param {string} notes - Notes to add
   */
  updateCallNotes: async (teamId, callId, notes) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (typeof window !== "undefined") {
        try {
          const auth = getAuth();
          const currentUser = auth.currentUser;

          if (currentUser) {
            const token = await currentUser.getIdToken(true);
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (tokenError) {
          console.warn("Unable to obtain auth token:", tokenError);
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/updateCallNotes`,
        { teamId, callId, notes },
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error("Error updating call notes:", error.response || error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          "Failed to update call notes"
      );
    }
  },
};

export default RealTimeVoiceService;
